import { useQuery } from '@tanstack/react-query';
import axiosInstance from '../lib/axios';

export type SubscriptionTier = 'free' | 'starter' | 'professional' | 'enterprise';

export interface TierLimits {
  maxProducts: number;
  currentProducts: number;
  maxClients: number;
  currentClients: number;
  salesRateLimit: number;
  maxLocations: number;
  currentLocations: number;
  includedStores: number;
  currentStores: number;
  includedWarehouses: number;
  currentWarehouses: number;
  includedStaff: number;
  currentStaff: number;
}

export interface TierAccessResponse {
  tier: SubscriptionTier;
  limits: TierLimits;
  isVirtualOnly: boolean;
  hasStorefrontAccess: boolean;
  hasAnalyticsAccess: boolean;
}

export interface UseTierAccessResult {
  tier: SubscriptionTier;
  limits: TierLimits | undefined;
  isLoading: boolean;
  error: Error | null;
  isVirtualOnly: boolean;
  hasStorefrontAccess: boolean;
  hasAnalyticsAccess: boolean;
  hasTierAccess: (requiredTier: SubscriptionTier) => boolean;
  isAtLimit: (resource: keyof TierLimits) => boolean;
  getRemainingQuota: (resource: keyof TierLimits) => number;
  isUnlimited: (resource: keyof TierLimits) => boolean;
}

// Tier hierarchy for comparison
const TIER_ORDER: Record<SubscriptionTier, number> = {
  free: 0,
  starter: 1,
  professional: 2,
  enterprise: 3,
};

/**
 * Hook to check the current organization's subscription tier and limits
 * @returns Object with tier info, limits, and helper functions
 */
export const useTierAccess = (): UseTierAccessResult => {
  const { data, isLoading, error } = useQuery<TierAccessResponse>({
    queryKey: ['organizationLimits'],
    queryFn: async () => {
      try {
        const response = await axiosInstance.get('/organizations/me/limits');
        return response.data;
      } catch (err) {
        console.error('[useTierAccess] Failed to load tier limits:', err);
        // Return default FREE tier limits on error
        return {
          tier: 'free' as SubscriptionTier,
          limits: {
            maxProducts: 50,
            currentProducts: 0,
            maxClients: 100,
            currentClients: 0,
            salesRateLimit: 10,
            maxLocations: 1,
            currentLocations: 0,
            includedStores: 1,
            currentStores: 0,
            includedWarehouses: 0,
            currentWarehouses: 0,
            includedStaff: 2,
            currentStaff: 0,
          },
          isVirtualOnly: true,
          hasStorefrontAccess: false,
          hasAnalyticsAccess: false,
        };
      }
    },
    staleTime: 5 * 60 * 1000, // Cache for 5 minutes
    retry: false,
  });

  const tier = data?.tier ?? 'free';
  const limits = data?.limits;

  /**
   * Check if current tier has access to a required tier level
   * @param requiredTier - Minimum tier required
   * @returns true if current tier >= required tier
   */
  const hasTierAccess = (requiredTier: SubscriptionTier): boolean => {
    return TIER_ORDER[tier] >= TIER_ORDER[requiredTier];
  };

  /**
   * Check if a resource limit has been reached
   * @param resource - The limit key to check (e.g., 'maxProducts')
   * @returns true if at or over limit
   */
  const isAtLimit = (resource: keyof TierLimits): boolean => {
    if (!limits) return false;
    const maxKey = resource.startsWith('max') || resource.startsWith('included')
      ? resource
      : `max${resource.charAt(0).toUpperCase()}${resource.slice(1)}` as keyof TierLimits;
    const currentKey = resource.startsWith('current')
      ? resource
      : `current${resource.replace('max', '').replace('included', '')}` as keyof TierLimits;

    const max = limits[maxKey] as number;
    const current = limits[currentKey] as number;

    // -1 means unlimited
    if (max === -1) return false;
    return current >= max;
  };

  /**
   * Get remaining quota for a resource
   * @param resource - The limit key to check
   * @returns Number of remaining items, -1 for unlimited
   */
  const getRemainingQuota = (resource: keyof TierLimits): number => {
    if (!limits) return 0;

    // Map resource to max/current pairs
    const pairs: Record<string, [keyof TierLimits, keyof TierLimits]> = {
      products: ['maxProducts', 'currentProducts'],
      maxProducts: ['maxProducts', 'currentProducts'],
      currentProducts: ['maxProducts', 'currentProducts'],
      clients: ['maxClients', 'currentClients'],
      maxClients: ['maxClients', 'currentClients'],
      currentClients: ['maxClients', 'currentClients'],
      locations: ['maxLocations', 'currentLocations'],
      maxLocations: ['maxLocations', 'currentLocations'],
      currentLocations: ['maxLocations', 'currentLocations'],
      stores: ['includedStores', 'currentStores'],
      includedStores: ['includedStores', 'currentStores'],
      currentStores: ['includedStores', 'currentStores'],
      warehouses: ['includedWarehouses', 'currentWarehouses'],
      includedWarehouses: ['includedWarehouses', 'currentWarehouses'],
      currentWarehouses: ['includedWarehouses', 'currentWarehouses'],
      staff: ['includedStaff', 'currentStaff'],
      includedStaff: ['includedStaff', 'currentStaff'],
      currentStaff: ['includedStaff', 'currentStaff'],
    };

    const pair = pairs[resource as string];
    if (!pair) return 0;

    const max = limits[pair[0]] as number;
    const current = limits[pair[1]] as number;

    // -1 means unlimited
    if (max === -1) return -1;
    return Math.max(0, max - current);
  };

  /**
   * Check if a resource has unlimited quota
   * @param resource - The limit key to check
   * @returns true if unlimited (-1)
   */
  const isUnlimited = (resource: keyof TierLimits): boolean => {
    if (!limits) return false;
    const maxKey = resource.startsWith('max') || resource.startsWith('included')
      ? resource
      : `max${resource.charAt(0).toUpperCase()}${resource.slice(1)}` as keyof TierLimits;
    return (limits[maxKey] as number) === -1;
  };

  return {
    tier,
    limits,
    isLoading,
    error: error as Error | null,
    isVirtualOnly: data?.isVirtualOnly ?? true,
    hasStorefrontAccess: data?.hasStorefrontAccess ?? false,
    hasAnalyticsAccess: data?.hasAnalyticsAccess ?? false,
    hasTierAccess,
    isAtLimit,
    getRemainingQuota,
    isUnlimited,
  };
};

/**
 * Convenience hooks for common tier checks
 */
export const useIsFreeTier = (): boolean => {
  const { tier } = useTierAccess();
  return tier === 'free';
};

export const useIsProTier = (): boolean => {
  const { tier, hasTierAccess } = useTierAccess();
  return tier === 'professional' || hasTierAccess('professional');
};

export const useIsStarterTier = (): boolean => {
  const { tier } = useTierAccess();
  return tier === 'starter';
};

export const useIsEnterpriseTier = (): boolean => {
  const { tier } = useTierAccess();
  return tier === 'enterprise';
};
